import React, { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FaEye, FaEyeSlash, FaPhone, FaLock, FaShieldAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import loginImg from '../assets/back.png';
import signUpImg from '../assets/back.png';

const BASE_URL = "https://api.dreamtoday.in";

/* ══════════════════════════════════════
   FORGOT PASSWORD MODAL
   Step 1: Enter phone  → API sends OTP
   Step 2: Enter OTP + new password → reset
══════════════════════════════════════ */
const ForgotPasswordModal = ({ onClose }) => {
  const [step, setStep] = useState(1); // 1 = phone, 2 = otp + new password
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [receivedOtp, setReceivedOtp] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // ── Step 1: Request OTP
  const handleRequestOtp = async () => {
    if (!phone.trim()) { setError("Please enter your mobile number."); return; }
    if (!/^\d{10}$/.test(phone.trim())) { setError("Enter a valid 10-digit mobile number."); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch(`${BASE_URL}/api/users/forgot-password-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: phone.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        setReceivedOtp(data.otp);
        setStep(2);
      } else {
        setError(data.message || "Failed to send OTP. Please try again.");
      }
    } catch { setError("Network error. Please try again."); }
    finally { setLoading(false); }
  };

  // ── Step 2: Reset password
  const handleResetPassword = async () => {
    if (!otp.trim()) { setError("Please enter the OTP."); return; }
    if (!newPassword.trim()) { setError("Please enter a new password."); return; }
    if (newPassword.trim().length < 5) { setError("Password must be at least 5 characters."); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch(`${BASE_URL}/api/users/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: phone.trim(), otp: otp, newPassword: newPassword.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        setSuccess(true);
      } else {
        setError(data.message || "Reset failed. Please try again.");
      }
    } catch { setError("Network error. Please try again."); }
    finally { setLoading(false); }
  };

  const inputCls = "w-full px-4 py-3 rounded-xl border border-yellow-200 outline-none text-gray-900 focus:border-yellow-400 focus:ring-2 focus:ring-yellow-100 transition text-sm";

  const content = (
    <motion.div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="bg-white rounded-3xl p-7 w-full max-w-sm shadow-2xl border border-yellow-100"
        initial={{ scale: 0.85, y: 30 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.85, y: 30 }}
        onClick={(e) => e.stopPropagation()}
      >
        {success ? (
          /* ── SUCCESS STATE ── */
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full bg-green-50 border-2 border-green-200
              flex items-center justify-center mx-auto mb-4">
              <FaShieldAlt className="text-green-500 text-2xl" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Password Reset!</h3>
            <p className="text-gray-500 text-sm mb-6">Your password has been updated successfully. You can now login.</p>
            <button
              onClick={onClose}
              className="w-full py-3 rounded-xl text-white font-semibold
                bg-gradient-to-r from-yellow-500 to-amber-600 shadow hover:scale-[1.02] transition"
            >
              Back to Login
            </button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-yellow-400 to-amber-500
                flex items-center justify-center shadow-md shrink-0">
                <FaLock className="text-white text-base" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-800">Forgot Password</h3>
                <p className="text-xs text-gray-400">
                  {step === 1 ? "Enter your registered mobile number" : "Enter OTP & set new password"}
                </p>
              </div>
            </div>

            {/* Step Indicator */}
            <div className="flex items-center gap-2 mb-6">
              {[1, 2].map((s) => (
                <React.Fragment key={s}>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${step >= s
                      ? "bg-gradient-to-br from-yellow-400 to-amber-500 text-white shadow-sm"
                      : "bg-gray-100 text-gray-400"
                    }`}>{s}</div>
                  {s < 2 && <div className={`flex-1 h-0.5 rounded transition-all ${step >= 2 ? "bg-yellow-400" : "bg-gray-200"}`} />}
                </React.Fragment>
              ))}
              <span className="text-xs text-gray-400 ml-1">{step === 1 ? "Phone" : "Reset"}</span>
            </div>

            {/* STEP 1 — Phone */}
            {step === 1 && (
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-gray-500 font-medium mb-1 block">Mobile Number</label>
                  <div className="relative">
                    <FaPhone className="absolute left-4 top-1/2 -translate-y-1/2 text-yellow-500 text-sm" />
                    <input
                      placeholder="10-digit mobile number"
                      value={phone}
                      maxLength={10}
                      onChange={(e) => { setPhone(e.target.value.replace(/\D/g, "")); setError(""); }}
                      onKeyDown={(e) => e.key === "Enter" && handleRequestOtp()}
                      className={`${inputCls} pl-10`}
                    />
                  </div>
                </div>

                {error && <p className="text-red-500 text-xs">{error}</p>}

                <button
                  onClick={handleRequestOtp}
                  disabled={loading}
                  className="w-full py-3 rounded-xl text-white font-semibold text-sm
                    bg-gradient-to-r from-yellow-500 to-amber-600 shadow hover:scale-[1.02]
                    transition disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {loading
                    ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Sending OTP...</>
                    : "Send OTP →"
                  }
                </button>
              </div>
            )}

            {/* STEP 2 — OTP + New Password */}
            {step === 2 && (
              <div className="space-y-4">
                {/* Show received OTP */}
                {receivedOtp && (
                  <div className="px-4 py-3 rounded-xl bg-yellow-50 border border-yellow-200 text-center">
                    <p className="text-xs text-yellow-600 font-medium mb-0.5">Your OTP</p>
                    <p className="text-2xl font-black tracking-[0.25em] text-yellow-700">{receivedOtp}</p>
                  </div>
                )}

                <div>
                  <label className="text-xs text-gray-500 font-medium mb-1 block">Enter OTP</label>
                  <input
                    placeholder="Enter OTP"
                    value={otp}
                    onChange={(e) => { setOtp(e.target.value.replace(/\D/g, "")); setError(""); }}
                    maxLength={6}
                    className={inputCls}
                  />
                </div>

                <div>
                  <label className="text-xs text-gray-500 font-medium mb-1 block">New Password</label>
                  <div className="relative">
                    <input
                      type={showPass ? "text" : "password"}
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => { setNewPassword(e.target.value); setError(""); }}
                      onKeyDown={(e) => e.key === "Enter" && handleResetPassword()}
                      className={`${inputCls} pr-11`}
                    />
                    <button
                      onClick={() => setShowPass(!showPass)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-yellow-600"
                    >
                      {showPass ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                {error && <p className="text-red-500 text-xs">{error}</p>}

                <div className="flex gap-3">
                  <button
                    onClick={() => { setStep(1); setOtp(""); setNewPassword(""); setError(""); }}
                    className="flex-1 py-3 rounded-xl border border-yellow-200 text-yellow-700 font-semibold
                      hover:bg-yellow-50 transition text-sm"
                  >
                    ← Back
                  </button>
                  <button
                    onClick={handleResetPassword}
                    disabled={loading}
                    className="flex-1 py-3 rounded-xl text-white font-semibold text-sm
                      bg-gradient-to-r from-yellow-500 to-amber-600 shadow hover:scale-[1.02]
                      transition disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {loading
                      ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Resetting...</>
                      : "Reset Password"
                    }
                  </button>
                </div>
              </div>
            )}

            {/* Cancel */}
            {step === 1 && (
              <button onClick={onClose} className="w-full mt-3 text-center text-sm text-gray-400 hover:text-gray-600 transition">
                Cancel
              </button>
            )}
          </>
        )}
      </motion.div>
    </motion.div>
  );

  return createPortal(content, document.body);
};

/* ══════════════════════════════════════
   AUTH PAGE
══════════════════════════════════════ */
const AuthPage = () => {
  const [isSignup, setIsSignup] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [pendingUserId, setPendingUserId] = useState(null);
  const [receivedOtp, setReceivedOtp] = useState(null);
  const navigate = useNavigate();

  const openOtp = (userId, otp) => {
    setPendingUserId(userId);
    setReceivedOtp(otp);
    setShowOtpModal(true);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden
      bg-gradient-to-b from-[#fffdf5] via-[#fff6d6] to-[#fffdf5]">

      {/* GOLD GRID */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 opacity-100 pointer-events-none
          [background-image:linear-gradient(rgba(212,175,55,0.15)_1px,transparent_1px),linear-gradient(90deg,rgba(212,175,55,0.15)_1px,transparent_1px)]
          [background-size:60px_60px]"
        />
        {/* {[...Array(50)].map((_, i) => (
          <div
            key={i}
            className="coin text-yellow-400 text-2xl z-10 absolute animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              animationDuration: `${4 + Math.random() * 6}s`,
              animationDelay: `${Math.random() * 5}s`,
            }}
          >
            <div className="relative w-8 h-8 flex items-center justify-center rounded-full
                bg-gradient-to-br from-yellow-200 via-yellow-500 to-yellow-800
                shadow-[0_0_12px_rgba(255,215,0,0.9)] border border-yellow-700">
              <div className="absolute inset-[3px] rounded-full border border-yellow-300/60"></div>
              <span className="relative text-[15px] font-black text-yellow-50 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">$</span>
            </div>
          </div>
        ))} */}
      </div>

      {/* MAIN CONTAINER */}
      <div className="relative w-full max-w-6xl rounded-[40px] overflow-hidden shadow-2xl bg-white/70 backdrop-blur-xl">

        {/* DESKTOP LAYOUT */}
        <div className="hidden lg:block relative h-[650px]">

          {/* LEFT INFO PANEL */}
          <motion.div
            animate={{ x: isSignup ? "100%" : "0%" }}
            transition={{ duration: 0.8 }}
            className="absolute left-0 top-0 w-1/2 h-full p-16 flex flex-col items-center justify-center text-center overflow-hidden"
          >
            <motion.div
              key={isSignup ? "signup" : "login"}
              initial={{ opacity: 0, scale: 1.1 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              className="absolute inset-0"
              style={{
                backgroundImage: `url(${isSignup ? loginImg : signUpImg})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-br from-black/40 via-black/0 to-black/40" />
            <div className="absolute inset-0 bg-gradient-to-tr from-white/20 via-transparent to-transparent" />

            <div className="relative z-10">
              <div className="relative mb-10">
                <div className="absolute inset-0 bg-yellow-400/30 blur-3xl rounded-full"></div>
                <img src="/logo.png" alt="PrizePulse" className="relative h-24 md:h-28 object-contain drop-shadow-xl" />
              </div>
              <button
                onClick={() => setIsSignup(!isSignup)}
                className="px-8 py-3 rounded-full text-white font-semibold
                  bg-gradient-to-r from-yellow-500 to-amber-600 shadow-lg hover:scale-105 transition"
              >
                {isSignup ? "Login" : "Register"}
              </button>
            </div>
          </motion.div>

          {/* RIGHT FORM PANEL */}
          <motion.div
            animate={{ x: isSignup ? "-100%" : "0%" }}
            transition={{ duration: 0.8 }}
            className="absolute right-0 top-0 w-1/2 h-full bg-white p-16 flex items-center"
          >
            <Form
              isSignup={isSignup}
              openOtp={openOtp}
              navigate={navigate}
              onForgotPassword={() => setShowForgotModal(true)}
            />
          </motion.div>
        </div>

        {/* MOBILE LAYOUT */}
        <div className="lg:hidden p-6">
          <Form
            isSignup={isSignup}
            openOtp={openOtp}
            navigate={navigate}
            onForgotPassword={() => setShowForgotModal(true)}
          />
          <button
            onClick={() => setIsSignup(!isSignup)}
            className="mt-6 text-yellow-700 font-semibold w-full text-center"
          >
            {isSignup ? "Already have account? Login" : "New user? Register"}
          </button>
        </div>
      </div>

      {/* OTP MODAL */}
      <AnimatePresence>
        {showOtpModal && (
          <OtpModal
            pendingUserId={pendingUserId}
            receivedOtp={receivedOtp}
            onClose={() => setShowOtpModal(false)}
            onVerified={() => { setShowOtpModal(false); setIsSignup(false); }}
          />
        )}
      </AnimatePresence>

      {/* FORGOT PASSWORD MODAL */}
      <AnimatePresence>
        {showForgotModal && (
          <ForgotPasswordModal onClose={() => setShowForgotModal(false)} />
        )}
      </AnimatePresence>
    </div>
  );
};

/* ══════════════════════════════════════
   OTP MODAL — unchanged
══════════════════════════════════════ */
const OtpModal = ({ pendingUserId, receivedOtp, onClose, onVerified }) => {
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleVerify = async () => {
    if (!otp.trim()) { setError("Please enter OTP"); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch(`${BASE_URL}/api/users/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ otp }),
      });
      const data = await res.json();
      if (data.success) onVerified();
      else setError(data.message || "Invalid OTP. Please try again.");
    } catch { setError("Network error. Please try again."); }
    finally { setLoading(false); }
  };

  return (
    <motion.div
      className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-white rounded-3xl p-6 w-full max-w-sm shadow-2xl"
        initial={{ scale: 0.8 }} animate={{ scale: 1 }} exit={{ scale: 0.8 }}
      >
        <h3 className="text-xl font-bold mb-2 text-yellow-700">Verify OTP</h3>
        <p className="text-sm text-gray-500 mb-3">Enter the OTP sent to your mobile number.</p>

        {receivedOtp && (
          <div className="mb-4 px-4 py-3 rounded-xl bg-yellow-50 border border-yellow-300 text-center">
            <p className="text-xs text-yellow-600 font-medium mb-1">Your OTP</p>
            <p className="text-3xl font-black tracking-[0.3em] text-yellow-700">{receivedOtp}</p>
          </div>
        )}

        <input
          placeholder="Enter OTP"
          value={otp}
          onChange={(e) => setOtp(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleVerify()}
          className="w-full px-4 py-3 rounded-xl border border-yellow-200 mb-2 outline-none text-gray-900"
        />
        {error && <p className="text-red-500 text-sm mb-3">{error}</p>}

        <div className="flex gap-3">
          <button onClick={onClose}
            className="flex-1 py-3 rounded-full font-semibold border border-yellow-300 text-yellow-700 hover:bg-yellow-50 transition">
            Cancel
          </button>
          <button onClick={handleVerify} disabled={loading}
            className="flex-1 py-3 rounded-full text-white font-semibold bg-gradient-to-r from-yellow-500 to-amber-600 disabled:opacity-60">
            {loading ? "Verifying..." : "Verify"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

/* ══════════════════════════════════════
   FORM — forgot password link added
══════════════════════════════════════ */
const Form = ({ isSignup, openOtp, navigate, onForgotPassword }) => {
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [regPhone, setRegPhone] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [loginPhone, setLoginPhone] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  const inputStyle =
    "w-full px-4 py-3 rounded-xl border border-yellow-200 mb-4 outline-none text-gray-900 focus:border-yellow-400 transition";

  const handleRegister = async () => {
    if (!name || !email || !regPhone || !regPassword) { setError("All fields are required."); return; }
    if (!/^\d{10}$/.test(regPhone)) { setError("Mobile number must be exactly 10 digits."); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch(`${BASE_URL}/api/users/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, phone: regPhone, password: regPassword }),
      });
      const data = await res.json();
      if (data.success) openOtp(data.userId, data.otp);
      else setError(data.message || "Registration failed. Please try again.");
    } catch { setError("Network error. Please try again."); }
    finally { setLoading(false); }
  };

  const handleLogin = async () => {
    if (!loginPhone || !loginPassword) { setError("Phone and password are required."); return; }
    if (!/^\d{10}$/.test(loginPhone)) { setError("Mobile number must be exactly 10 digits."); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch(`${BASE_URL}/api/users/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: loginPhone, password: loginPassword }),
      });
      const data = await res.json();
      if (data.success) {
        sessionStorage.setItem("isLoggedIn", "true");
        sessionStorage.setItem("userId", data.userId);
        sessionStorage.setItem("userName", data.name);
        sessionStorage.setItem("userPhone", data.phone);
        sessionStorage.setItem("userEmail", data.email);
        navigate("/home");
      } else setError(data.message || "Invalid credentials. Please try again.");
    } catch { setError("Network error. Please try again."); }
    finally { setLoading(false); }
  };

  React.useEffect(() => { setError(""); }, [isSignup]);

  return (
    <div className="w-full">
      <h2 className="text-3xl font-bold mb-6 text-center text-gray-900">
        {isSignup ? "Create Account" : "Login"}
      </h2>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">{error}</div>
      )}

      {isSignup ? (
        <>
          <input placeholder="Full Name" className={inputStyle} value={name} onChange={(e) => setName(e.target.value)} />
          <input placeholder="Email" type="email" className={inputStyle} value={email} onChange={(e) => setEmail(e.target.value)} />
          <input
            placeholder="Mobile Number (10 digits)"
            className={inputStyle}
            value={regPhone}
            maxLength={10}
            onChange={(e) => setRegPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
          />
          <div className="relative">
            <input type={showPass ? "text" : "password"} placeholder="Password" className={inputStyle}
              value={regPassword} onChange={(e) => setRegPassword(e.target.value)} />
            <button onClick={() => setShowPass(!showPass)} className="absolute right-4 top-4 text-yellow-700">
              {showPass ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
          <button onClick={handleRegister} disabled={loading}
            className="bg-gradient-to-r from-yellow-500 to-amber-600 text-white w-full py-3 rounded-full font-semibold disabled:opacity-60 transition">
            {loading ? "Registering..." : "Register"}
          </button>
        </>
      ) : (
        <>
          <input
            placeholder="Mobile Number (10 digits)"
            className={inputStyle}
            value={loginPhone}
            maxLength={10}
            onChange={(e) => setLoginPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
          />
          <div className="relative">
            <input type={showPass ? "text" : "password"} placeholder="Password" className={inputStyle}
              value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleLogin()} />
            <button onClick={() => setShowPass(!showPass)} className="absolute right-4 top-4 text-yellow-700">
              {showPass ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>

          {/* FORGOT PASSWORD LINK */}
          <div className="flex justify-end -mt-2 mb-4">
            <button
              onClick={onForgotPassword}
              className="text-sm text-yellow-600 hover:text-yellow-800 font-medium hover:underline transition"
            >
              Forgot Password?
            </button>
          </div>

          <button onClick={handleLogin} disabled={loading}
            className="bg-gradient-to-r from-yellow-500 to-amber-600 text-white w-full py-3 rounded-full font-semibold disabled:opacity-60 transition">
            {loading ? "Logging in..." : "Login"}
          </button>
        </>
      )}
    </div>
  );
};

export default AuthPage;